import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Camera, LogIn, LogOut, MapPin, Clock, QrCode, History, Download, AlertCircle } from "lucide-react";

function formatDuration(start, end) {
  const s = new Date(start);
  const e = end ? new Date(end) : new Date();
  const mins = Math.round((e - s) / 60000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function Scanner({ onScan, onClose }) {
  const divId = "qr-reader";
  const scannerRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const html5 = new Html5Qrcode(divId);
    scannerRef.current = html5;
    html5
      .start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        (decoded) => {
          onScan(decoded);
        },
        () => {}
      )
      .catch((e) => setError(e.message || String(e)));

    return () => {
      try {
        if (scannerRef.current && scannerRef.current.isScanning) {
          scannerRef.current.stop().then(() => scannerRef.current.clear()).catch(() => {});
        }
      } catch {}
    };
  }, [onScan]);

  return (
    <div className="relative" data-testid="scanner">
      <div id={divId} className="w-full rounded-2xl overflow-hidden bg-black aspect-square" />
      {error && (
        <div className="absolute inset-0 bg-black/80 text-white flex flex-col items-center justify-center p-4 rounded-2xl">
          <AlertCircle className="w-8 h-8 mb-3" />
          <div className="font-semibold text-sm mb-1">Camera unavailable</div>
          <div className="text-xs text-white/70 text-center">{error}</div>
        </div>
      )}
      <div className="absolute inset-0 pointer-events-none rounded-2xl border-4 border-primary/30" />
    </div>
  );
}

export default function MyQr() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [scanning, setScanning] = useState(false);
  const [myQrUrl, setMyQrUrl] = useState(null);

  const { data: shiftData } = useQuery({
    queryKey: ["my-shift"],
    queryFn: async () => (await api.get("/shifts/me")).data,
    refetchInterval: 15000,
  });

  useEffect(() => {
    let url = null;
    api.get(`/employees/${user.id}/qr`, { responseType: "blob" }).then((r) => {
      url = URL.createObjectURL(r.data);
      setMyQrUrl(url);
    }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [user.id]);

  const handleScan = async (decoded) => {
    setScanning(false);
    try {
      const res = await api.post("/shifts/scan", { payload: decoded });
      const { action, shift, area, facility } = res.data;
      toast.success(action === "clock_in"
        ? `Clocked in at ${area.name}`
        : `Clocked out · ${formatDuration(shift.clock_in_at, shift.clock_out_at)}`);
      qc.invalidateQueries({ queryKey: ["my-shift"] });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadMyQr = async () => {
    try {
      const res = await api.get(`/employees/${user.id}/qr`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `my-staff-qr-${user.name.replace(/\s+/g, "_")}.png`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const current = shiftData?.current;
  const history = shiftData?.history || [];
  const totalHoursToday = history.filter((h) => {
    const d = new Date(h.clock_in_at);
    const now = new Date();
    return d.toDateString() === now.toDateString();
  }).reduce((sum, h) => {
    const start = new Date(h.clock_in_at);
    const end = h.clock_out_at ? new Date(h.clock_out_at) : new Date();
    return sum + (end - start) / 3600000;
  }, 0);

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto md:mx-0">
      <div className="mb-6">
        <div className="label-caps text-muted-foreground mb-2">Attendance</div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">My Shift</h1>
        <p className="text-sm text-muted-foreground mt-2">Scan a location QR to clock in or out.</p>
      </div>

      {/* Current status card */}
      <div
        className={`rounded-2xl p-6 mb-6 relative overflow-hidden ${
          current ? "bg-primary text-primary-foreground" : "bg-card border border-border"
        }`}
        data-testid="shift-status-card"
      >
        {current ? (
          <>
            <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-white/10" />
            <div className="relative">
              <div className="text-[0.7rem] tracking-widest uppercase opacity-80 font-semibold flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-white animate-pulse" /> On shift
              </div>
              <div className="font-display text-3xl font-black mt-2">{formatDuration(current.clock_in_at)}</div>
              <div className="flex items-center gap-2 mt-2 text-sm opacity-90">
                <MapPin className="w-4 h-4" /> {current.area_name} · {current.facility_name}
              </div>
              <div className="text-xs opacity-70 mt-1">
                Started {new Date(current.clock_in_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
              </div>
              <Button
                size="lg"
                variant="secondary"
                className="mt-5 rounded-2xl h-12 px-6 bg-white text-primary hover:bg-white/90 font-semibold"
                onClick={() => setScanning(true)}
                data-testid="scan-clock-out"
              >
                <Camera className="w-5 h-5 mr-2" />Scan to Clock Out
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="text-[0.7rem] tracking-widest uppercase text-muted-foreground font-semibold">Not clocked in</div>
            <div className="font-display text-2xl font-bold mt-2">Scan a location QR to start</div>
            <p className="text-sm text-muted-foreground mt-2">
              Every area in the facility has a QR tag. Scan it to log your arrival.
            </p>
            <Button
              size="lg"
              className="mt-5 rounded-2xl h-12 px-6 font-semibold"
              onClick={() => setScanning(true)}
              data-testid="scan-clock-in"
            >
              <Camera className="w-5 h-5 mr-2" />Scan to Clock In
            </Button>
          </>
        )}
      </div>

      {/* Today summary */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="rounded-2xl border border-border bg-card p-4 text-center">
          <div className="label-caps text-muted-foreground text-[0.6rem]">Today</div>
          <div className="font-display text-2xl font-black mt-1">{totalHoursToday.toFixed(1)}h</div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-4 text-center">
          <div className="label-caps text-muted-foreground text-[0.6rem]">Shifts</div>
          <div className="font-display text-2xl font-black mt-1">{history.filter((h) => new Date(h.clock_in_at).toDateString() === new Date().toDateString()).length}</div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-4 text-center">
          <div className="label-caps text-muted-foreground text-[0.6rem]">Status</div>
          <div className={`font-display text-sm font-black mt-2 ${current ? "text-primary" : "text-muted-foreground"}`}>{current ? "ACTIVE" : "OFF"}</div>
        </div>
      </div>

      {/* My QR card */}
      <div className="border border-border bg-card rounded-2xl p-5 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-xl border border-border bg-white p-1 flex items-center justify-center shrink-0">
            {myQrUrl ? <img src={myQrUrl} alt="QR" className="w-full h-full" /> : <QrCode className="w-6 h-6 text-muted-foreground" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="label-caps text-muted-foreground">My ID Card</div>
            <div className="font-semibold text-sm truncate">{user.name}</div>
            <div className="text-xs text-muted-foreground truncate">{user.position || user.role}</div>
          </div>
          <Button variant="outline" size="sm" className="rounded-xl" onClick={downloadMyQr} data-testid="download-my-qr">
            <Download className="w-3.5 h-3.5 mr-1" />Save
          </Button>
        </div>
      </div>

      {/* History */}
      <div className="border border-border bg-card rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div>
            <div className="label-caps text-muted-foreground">Recent shifts</div>
            <div className="font-display text-base font-semibold">Last {history.length}</div>
          </div>
          <History className="w-4 h-4 text-muted-foreground" />
        </div>
        <div className="divide-y divide-border">
          {history.map((s) => (
            <div key={s.id} className="px-5 py-3 flex items-center justify-between text-sm" data-testid={`shift-${s.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{s.area_name}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {new Date(s.clock_in_at).toLocaleString()}
                  {s.clock_out_at && ` → ${new Date(s.clock_out_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`}
                </div>
              </div>
              <div className="text-xs font-semibold shrink-0 ml-2">
                {s.clock_out_at
                  ? formatDuration(s.clock_in_at, s.clock_out_at)
                  : <span className="text-primary">Active</span>}
              </div>
            </div>
          ))}
          {history.length === 0 && (
            <div className="p-10 text-center text-sm text-muted-foreground">No shifts yet.</div>
          )}
        </div>
      </div>

      <Dialog open={scanning} onOpenChange={setScanning}>
        <DialogContent className="max-w-md p-4">
          <DialogHeader><DialogTitle>Scan location QR</DialogTitle></DialogHeader>
          <Scanner onScan={handleScan} onClose={() => setScanning(false)} />
          <p className="text-xs text-muted-foreground text-center">
            Point your camera at the location QR tag.
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
